/**
 * CalendarSystemRow — a `ComputedDataSource` whose cells are
 * secondary-calendar labels (gregorian / lunar / hebrew / islamic /
 * persian / thai / ethiopian / indian) derived from the Gregorian date.
 *
 * Dispatched on wire type `"calendar-system"`. The row's
 * `attributes.calendarSystem` selects which calendar to display.
 * `computeCellsForRange` iterates every day in the range and
 * produces one cell per date using the matching `@tento-chrono`
 * converter.
 *
 * The one calendar-system computation: `calendarSystemCellData` is also
 * what the SDK's `CellProvider` (`src/secondary-calendars/provider.ts`)
 * adapts, so the two never normalize or label a day differently. The
 * per-system dispatch is a runtime attribute lookup, not a per-system
 * class (one row type, N attribute values).
 */

import type { Option } from "../../core/option.ts";
import { Json } from "../../core/json.ts";
import {
  EthiopianConverter,
  HebrewConverter,
  IndianConverter,
  IslamicConverter,
  LunarConverter,
  NaiveDate,
  PersianConverter,
  ThaiConverter,
} from "@tento-chrono";

import type { CellEntry, OpaqueJson } from "../../core/dtype.ts";
import { ComputedDataSource, declareRecomputeMemo } from "./computed-data-source.ts";

const MAX_CALENDAR_SYSTEM_CACHE_ENTRIES = 4096;
const CALENDAR_SYSTEM_CELL_CACHE = new Map<string, OpaqueJson>();

type CalendarSystemCellData = Readonly<{
  calYear: number;
  calMonth: number;
  calDay: number;
  label: string;
}>;

/**
 * A computed source row (wire type `calendar-system`) whose cells are one secondary calendar's
 * labels for each Gregorian day.
 */
export class CalendarSystemRow extends ComputedDataSource {
  // Pure overwrite — the cells are a deterministic function of the date range
  // and the `calendarSystem` attribute alone (no external row reads, no
  // destructive pre-clear), so the memo is safe with an empty dependency
  // signature (this row reads no other row).
  static {
    declareRecomputeMemo(CalendarSystemRow, () => "");
  }

  override computeCellsForRange(range: NaiveDate.Range): CellEntry[] {
    const system = Json.string(this.attributes(), "calendarSystem") ?? "";
    const entries: CellEntry[] = [];

    for (const date of range.iter()) {
      const data = calendarSystemCellData(system, date);
      if (data == null) continue;
      const partial = new NaiveDate.Partial(
        "day",
        date.yr,
        date.month1,
        date.day1,
      );
      // `CalendarCellData` is `{ calYear, calMonth, calDay, label }`
      // — a Record of primitives, structurally an `OpaqueJson`.
      entries.push({ date: partial, id: null, value: data as OpaqueJson, style: null });
    }
    return entries;
  }
}

/**
 * The secondary-calendar label (lunar, hebrew, islamic, …) of one Gregorian date, the cell a
 * `CalendarSystemRow` computes (and the SDK's calendar-system provider adapts). A frozen value,
 * shared by every caller asking for the same day and system.
 */
export function calendarSystemCellData(
  rawSystem: string,
  date: NaiveDate,
): Option<OpaqueJson> {
  const system = normalizeCalendarSystemName(rawSystem);
  const key = `${system}:${date.dse}`;
  const cached = CALENDAR_SYSTEM_CELL_CACHE.get(key);
  if (cached !== undefined) return cached;

  const data = toCellData(system, date);
  if (data == null) return null;

  cacheCalendarSystemCell(key, data);
  return data;
}

/**
 * A `calendarSystem` attribute's name without its leading `:`, as `calendarSystemCellData` expects
 * it.
 */
export function normalizeCalendarSystemName(rawSystem: string): string {
  return rawSystem.startsWith(":") ? rawSystem.slice(1) : rawSystem;
}

function cacheCalendarSystemCell(key: string, data: OpaqueJson): void {
  if (CALENDAR_SYSTEM_CELL_CACHE.size >= MAX_CALENDAR_SYSTEM_CACHE_ENTRIES) {
    const firstKey = CALENDAR_SYSTEM_CELL_CACHE.keys().next().value;
    if (firstKey !== undefined) CALENDAR_SYSTEM_CELL_CACHE.delete(firstKey);
  }
  // Frozen: every caller of the same day and system shares this object.
  CALENDAR_SYSTEM_CELL_CACHE.set(key, Object.freeze(data));
}

function toCellData(system: string, date: NaiveDate): Option<OpaqueJson> {
  switch (system) {
    case "gregorian":
      return {
        calYear: date.yr,
        calMonth: date.month1,
        calDay: date.day1,
        label: String(date.day1),
      };
    case "lunar":
      return toOpaqueCellData(LunarConverter.toCellData(date) ?? null);
    case "hebrew":
      return toOpaqueCellData(HebrewConverter.toCellData(date) ?? null);
    case "islamic":
      return toOpaqueCellData(IslamicConverter.toCellData(date) ?? null);
    case "persian":
      return toOpaqueCellData(PersianConverter.toCellData(date) ?? null);
    case "thai":
      return toOpaqueCellData(ThaiConverter.toCellData(date) ?? null);
    case "ethiopian":
      return toOpaqueCellData(EthiopianConverter.toCellData(date) ?? null);
    case "indian":
      return toOpaqueCellData(IndianConverter.toCellData(date) ?? null);
    default:
      return null;
  }
}

function toOpaqueCellData(
  data: Option<CalendarSystemCellData>,
): Option<OpaqueJson> {
  if (data == null) return null;
  return {
    calYear: data.calYear,
    calMonth: data.calMonth,
    calDay: data.calDay,
    label: data.label,
  };
}
